import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Calendar } from 'lucide-react';

const POST_TYPE_BADGES = {
  lost: {
    label: 'Lost Pet',
    className: 'bg-red-100 text-red-700',
  },
  found: {
    label: 'Found Pet',
    className: 'bg-emerald-100 text-emerald-700',
  },
  psa: {
    label: 'PSA',
    className: 'bg-gray-100 text-gray-700',
  },
  petService: {
    label: 'Pet Service',
    className: 'bg-blue-100 text-blue-700',
  },
};

const formatPostDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const CommunityPostCard = ({ post, onSelect }) => {
  const badge = POST_TYPE_BADGES[post.type];
  const dateLabel = formatPostDate(post.date);

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={onSelect}
        className="w-full text-left bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
        aria-label={`Open post: ${post.title}`}
      >
        <div className="flex gap-3 p-4">
          {post.imageUrl ? (
            <img
              src={post.imageUrl}
              alt=""
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
          ) : null}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">
                {post.title}
              </h4>
              {badge ? (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${badge.className}`}
                >
                  {badge.label}
                </span>
              ) : null}
            </div>

            {post.description ? (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {post.description}
              </p>
            ) : null}

            {(post.location || dateLabel) && (
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                {post.location ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" aria-hidden="true" />
                    {post.location}
                  </span>
                ) : null}
                {post.location && dateLabel ? <span>·</span> : null}
                {dateLabel ? (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" aria-hidden="true" />
                    {dateLabel}
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
};

const CommunityPostCards = ({ posts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = Array.isArray(posts) ? posts : [];

  if (list.length === 0) return null;

  return (
    <section aria-label={t('related_posts', 'Related posts')}>
      <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
        {t('related_posts_in_app', 'Related Posts in App')}
      </h3>

      <ul className="space-y-3">
        {list.map((post) => (
          <CommunityPostCard
            key={post.id}
            post={post}
            onSelect={() =>
              navigate(`/post/${post.id}`, {
                state: { postType: post.type },
              })
            }
          />
        ))}
      </ul>
    </section>
  );
};

export default CommunityPostCards;
